import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import initParticles from '../constants/particles.js';
import './PurchaseSuccess.css';

const PurchaseSuccess = () => {
    const [token, setToken] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const canvasRef = useRef(null);
    const location = useLocation();
    localStorage.setItem("redirectAfterLogin", window.location.pathname);

    // Token Management
    const decodeToken = (token) => {
        try {
            return JSON.parse(atob(token.split(".")[1]));
        } catch (err) {
            console.error("Invalid token format:", err);
            return null;
        }
    };

    const verifyToken = (token) => {
        const decoded = decodeToken(token);
        if (!decoded) return false;
        const currentTime = Math.floor(Date.now() / 1000);
        return decoded.exp > currentTime;
    };

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.href = '/login';
    };

    const fetchAccessTokenIfNeeded = async () => {
        const token = localStorage.getItem("token");
        if (!token) return null;

        if (!verifyToken(token)) {
            try {
                const refreshToken = localStorage.getItem("refreshToken");
                const response = await axios.post("/api/auth/refresh-token", { refreshToken });
                const { accessToken } = response.data;
                localStorage.setItem("token", accessToken);
                return accessToken;
            } catch (err) {
                console.error("Failed to refresh token:", err);
                handleLogout();
                return null;
            }
        }
        return token;
    };

    // Fetch token from success endpoint
    useEffect(() => {
        const fetchToken = async () => {
            const sessionId = new URLSearchParams(location.search).get('session_id');
            if (!sessionId) {
                setError('Invalid or missing session ID.');
                setLoading(false);
                return;
            }

            // Retrieve or refresh the access token
            const authToken = await fetchAccessTokenIfNeeded();
            if (!authToken) {
                setError('You must be logged in to view this token. Please log in and try again.');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/auth/tokens/success?session_id=${sessionId}`, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${authToken}`,
                    },
                });
                if (!response.ok) {
                    const text = await response.text();
                    console.error('Non-JSON response:', text);
                    const data = text ? JSON.parse(text) : { message: 'Unknown error' };
                    if (response.status === 400 && data.message.includes('Session ID already used')) {
                        setToken(data.token);
                        setMessage(data.message);
                    } else if (response.status === 403) {
                        setError('You are not authorized to view this token.');
                    } else {
                        throw new Error(`Server returned ${response.status}: ${data.message || text.substring(0, 100)}...`);
                    }
                } else {
                    const data = await response.json();
                    setToken(data.token);
                    setMessage(data.message || 'Token created and sent successfully.');
                }
            } catch (err) {
                console.error('Fetch error:', err);
                setError(err.message || 'Failed to retrieve token. Please contact support.');
            } finally {
                setLoading(false);
            }
        };
        fetchToken();
    }, [location]);

    // Initialize particles
    useEffect(() => {
        if (canvasRef.current) {
            const cleanup = initParticles(canvasRef.current);
            return cleanup;
        }
    }, []);

    return (
        <div className="purchase-success-container">
            <div className="background-animation-wrapper">
                <canvas ref={canvasRef} className="particle-canvas"></canvas>
            </div>
            <div className="purchase-success">
                <h1 className="success-title">Purchase Successful!</h1>
                <p className="success-description">
                    {message.includes('already used')
                        ? 'This session was already used to create a token. The existing token is shown below and was sent to your email.'
                        : 'Thank you for your purchase. Your token has been sent to your email.'}
                </p>

                {loading ? (
                    <div className="loading-spinner">Loading...</div>
                ) : error ? (
                    <p className="error-message">{error}</p>
                ) : (
                    <div className="token-display">
                        <h2>Your Token</h2>
                        <div className="token-box">
                            <span className="token-text">{token}</span>
                            <button
                                className="copy-button"
                                onClick={() => {
                                    navigator.clipboard.writeText(token).then(() => {
                                        alert('Token copied to clipboard!');
                                    }).catch(() => {
                                        alert('Failed to copy token.');
                                    });
                                }}
                            >
                                Copy
                            </button>
                        </div>
                        <p className="token-instructions">
                            Use this token to activate your premium features. Keep it secure and check your email for a copy.
                        </p>
                    </div>
                )}

                <a href="/dashboard" className="dashboard-button">
                    Go to Dashboard
                </a>
            </div>
        </div>
    );
};

export default PurchaseSuccess;